import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Card,
} from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useUserPlan } from "../../context/UserPlanContext";
import supabase from "../../supabase";
import "./UserAccountPage.css";

const UserAccountPage = () => {
  const { user, loading } = useAuth();
  const { userPlan } = useUserPlan();

  const [profile, setProfile] = useState({ account_type: "Particulier" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [rechargeAmount, setRechargeAmount] = useState(5);
  const [loadingRecharge, setLoadingRecharge] = useState(false);

  const [billingCycle, setBillingCycle] = useState("annuel");
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [portalError, setPortalError] = useState("");

  const isArtisan = profile.account_type === "Artisan";
  const showStripeManagement = isArtisan || userPlan === "Pro";

  useEffect(() => {
    if (user?.id) fetchProfile(user.id);
  }, [user]);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("account_type")
      .eq("id", userId)
      .single();

    if (error) {
      setError("Erreur lors de la récupération du profil.");
    } else {
      setProfile(data);
    }
  };

  const handleUpgradeToPro = async () => {
    if (!user?.id) return;

    const plans = {
      Pro: {
        annuel: { price: "4,99€", stripeId: process.env.REACT_APP_STRIPEPRICE_ABOPRO_ANNUEL },
        mensuel: { price: "6,99€", stripeId: process.env.REACT_APP_STRIPEPRICE_ABOPRO_MENSUEL },
      },
    };

    const priceId = plans.Pro[billingCycle]?.stripeId;
    if (!priceId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            priceId,
            userId: user.id,
            email: user.email,
            plan: "Pro",
          }),
        }
      );

      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      setError("Erreur de connexion avec le serveur.");
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    setPortalError("");

    try {
      let stripeCustomerId = null;

      if (isArtisan) {
        const { data, error } = await supabase
          .from("artisans")
          .select("stripe_customer_id")
          .eq("email", user.email)
          .single();
        if (error || !data?.stripe_customer_id) throw new Error();
        stripeCustomerId = data.stripe_customer_id;
      } else {
        const { data, error } = await supabase
          .from("profiles")
          .select("stripe_customer_id")
          .eq("id", user.id)
          .single();
        if (error || !data?.stripe_customer_id) throw new Error();
        stripeCustomerId = data.stripe_customer_id;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/stripe/create-customer-portal-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId: stripeCustomerId }),
        }
      );

      const result = await response.json();
      if (result.url) window.location.href = result.url;
    } catch {
      setPortalError("Impossible d'accéder à votre abonnement Stripe.");
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    if (newPassword !== confirmPassword) {
      return setPasswordError("Les mots de passe ne correspondent pas.");
    }

    if (newPassword.length < 8) {
      return setPasswordError("Le mot de passe doit contenir au moins 8 caractères.");
    }

    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setPasswordMessage("Mot de passe mis à jour !");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      setPasswordError("Erreur lors du changement de mot de passe.");
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleRechargeRC = async (e) => {
    e.preventDefault();
    setLoadingRecharge(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/stripe/create-rc-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: rechargeAmount,
          email: user.email,
          userId: user.id,
        }),
      });

      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch {
      setError("Impossible de lancer la recharge RC.");
    } finally {
      setLoadingRecharge(false);
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (!user) return <p>Vous devez être connecté pour voir cette page.</p>;

  return (
    <Container className="mt-5 user-account-container">
      <h2 className="text-center user-account-title">Mon compte</h2>
      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="account-section-card">
            <Card.Body>
              <h5 className="account-section-title">Plan actuel : <strong>{userPlan}</strong></h5>

              {!isArtisan && userPlan === "Free" && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Choix de la formule Pro</Form.Label>
                    <Form.Select
                      value={billingCycle}
                      onChange={(e) => setBillingCycle(e.target.value)}
                    >
                      <option value="annuel">Annuel - 4,99€/mois</option>
                      <option value="mensuel">Mensuel - 6,99€/mois</option>
                    </Form.Select>
                  </Form.Group>

                  <Button variant="success" onClick={handleUpgradeToPro}>
                    Passer au plan Pro 🚀
                  </Button>
                </>
              )}

              {showStripeManagement && (
                <div className="mt-3">
                  <Button
                    variant="outline-primary"
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                  >
                    {managingSubscription
                      ? "Chargement..."
                      : "Gérer mon abonnement Stripe"}
                  </Button>
                  {portalError && (
                    <Alert variant="danger" className="mt-2">
                      {portalError}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {!isArtisan && (
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="account-section-card">
              <Card.Body>
                <h4 className="account-section-title">Recharger mes Renovestim Coins</h4>
                <Form onSubmit={handleRechargeRC}>
                  <Form.Group className="mb-3">
                    <Form.Label>Montant (€)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={rechargeAmount}
                      onChange={(e) => setRechargeAmount(Number(e.target.value))}
                      required
                    />
                    <Form.Text>1 € = 10 Renovestim Coins</Form.Text>
                  </Form.Group>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={loadingRecharge}
                  >
                    {loadingRecharge ? "Redirection..." : "Recharger"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="account-section-card">
            <Card.Body>
              <h4 className="account-section-title">Changer mon mot de passe</h4>
              {passwordMessage && <Alert variant="success">{passwordMessage}</Alert>}
              {passwordError && <Alert variant="danger">{passwordError}</Alert>}
              <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>Nouveau mot de passe</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirmation</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button variant="warning" type="submit" disabled={updatingPassword}>
                  {updatingPassword ? "Mise à jour..." : "Changer mon mot de passe"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserAccountPage;
